import {
  isCoarsenedContextAxes,
  pushLineSignal,
  type AttachmentDensity,
  type BinnedCount,
  type CoarsenedContextAxes,
  type CoarsenedPlanType,
  type CoarsenedWorkspaceType,
  type RawLineSignal,
  type TimelineSpan,
} from './idbLineSignals'

export type LineSignalKind =
  | 'PROPOSAL_BUILT'
  | 'PROPOSAL_REVISED'
  | 'PROPOSAL_DISCARDED'
  | 'LAGGING_USAGE_POSITIVE'
  | 'LAGGING_USAGE_NEGATIVE'
  | 'ATTACHMENT_INVENTORY_CURATED'
  | 'INTERVIEW_ESCALATION_FIRED'

/**
 * Callers may provide runtime facts only. User text, titles, labels, file
 * names, and attachment content do not belong in this shape.
 */
export interface LineSignalContextInput {
  planType?: string
  workspaceType?: string
  attachmentCount?: number
  timelineStart?: string | number | Date
  timelineEnd?: string | number | Date
  timelineDays?: number
  language?: string
  changeSomethingRounds?: number
  attachmentExtractedCount?: number
  attachmentKeptCount?: number
  /** Compatibility with the Plan 1 hooks; never persisted as dotted keys. */
  'attachment.extractedCount'?: number
  'attachment.keptCount'?: number
  round?: number
  layout?: 'mobile' | 'desktop'
  trigger?: 'scale' | 'conflict'
  preScaleCounts?: Record<string, number>
  /** Runtime-only routing key; never coarsened or persisted. */
  workspaceId?: string
}

const OPT_OUT_STORAGE_KEY = 'timelining-line-learning-signal-capture-disabled'
const workspaceCaptureState = new Map<string, { paused: boolean; enabled: boolean; killSwitch: boolean }>()

/** Fed by the session bridge so the hot signal path remains network-free. */
export const setLineWorkspaceSignalCaptureState = (workspaceId: string, state: { paused: boolean; enabled: boolean; killSwitch: boolean }) => {
  if (workspaceId) workspaceCaptureState.set(workspaceId, state)
}

const safeStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export const isLineSignalCaptureEnabled = () => safeStorage()?.getItem(OPT_OUT_STORAGE_KEY) !== 'true'

export const setLineSignalCaptureEnabled = (enabled: boolean) => {
  const storage = safeStorage()
  if (!storage) return
  if (enabled) storage.removeItem(OPT_OUT_STORAGE_KEY)
  else storage.setItem(OPT_OUT_STORAGE_KEY, 'true')
}

const toFiniteCount = (value: unknown) => {
  const count = Number(value)
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
}

const countBin = (value: unknown): BinnedCount => {
  const count = toFiniteCount(value)
  if (count === 0) return '0'
  if (count <= 10) return '1-10'
  if (count <= 40) return '11-40'
  return '40+'
}

const attachmentDensity = (value: unknown): AttachmentDensity => {
  const count = toFiniteCount(value)
  if (count === 0) return '0'
  if (count <= 3) return '1-3'
  if (count <= 10) return '4-10'
  return '10+'
}

const planType = (value: unknown): CoarsenedPlanType => {
  const normalized = typeof value === 'string' ? value.trim().toLocaleLowerCase() : ''
  if (['trip', 'travel', 'vacation'].includes(normalized)) return 'trip'
  if (['event', 'party', 'wedding'].includes(normalized)) return 'event'
  if (['project', 'work'].includes(normalized)) return 'project'
  if (['personal', 'home'].includes(normalized)) return 'personal'
  if (['health', 'fitness', 'medical'].includes(normalized)) return 'health'
  return 'other'
}

const workspaceType = (value: unknown): CoarsenedWorkspaceType => {
  const normalized = typeof value === 'string' ? value.trim().toLocaleLowerCase() : ''
  if (normalized === 'team') return 'team'
  if (normalized === 'org' || normalized === 'organization') return 'org'
  if (normalized === 'public-demo' || normalized === 'public_demo') return 'public-demo'
  return 'personal'
}

const topLevelLanguage = (value: unknown) => {
  const match = typeof value === 'string' ? value.trim().toLocaleLowerCase().match(/^([a-z]{2,3})(?:-[a-z0-9]+)*$/) : null
  return match?.[1] ?? 'en'
}

const parseDate = (value: unknown) => {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return Number.isFinite(value) ? value : Number.NaN
  if (typeof value === 'string') return Date.parse(value)
  return Number.NaN
}

const timelineSpan = (input: LineSignalContextInput): TimelineSpan => {
  const explicitDays = Number(input.timelineDays)
  const days = Number.isFinite(explicitDays)
    ? Math.max(1, Math.ceil(explicitDays))
    : (() => {
      const start = parseDate(input.timelineStart)
      const end = parseDate(input.timelineEnd)
      return Number.isFinite(start) && Number.isFinite(end)
        ? Math.max(1, Math.floor(Math.abs(end - start) / 86_400_000) + 1)
        : 1
    })()
  if (days <= 1) return 'day'
  if (days <= 7) return 'week'
  if (days <= 31) return 'month'
  if (days <= 92) return 'quarter'
  return 'longer'
}

const rounds = (value: unknown): CoarsenedContextAxes['changeSomethingRounds'] => {
  const count = toFiniteCount(value)
  return count >= 4 ? '4+' : count as 0 | 1 | 2 | 3
}

/** Converts potentially rich runtime metadata into the exact privacy-safe axes persisted locally. */
export const coarsenContextAxes = (input: LineSignalContextInput = {}): CoarsenedContextAxes => ({
  planType: planType(input.planType),
  workspaceType: workspaceType(input.workspaceType),
  attachmentDensity: attachmentDensity(input.attachmentCount),
  timelineSpan: timelineSpan(input),
  language: topLevelLanguage(input.language),
  changeSomethingRounds: rounds(input.changeSomethingRounds ?? input.round),
  attachmentExtractedCount: countBin(input.attachmentExtractedCount ?? input['attachment.extractedCount']),
  attachmentKeptCount: countBin(input.attachmentKeptCount ?? input['attachment.keptCount']),
})

const createSignalId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

export const emitLineSignal = (
  kind: LineSignalKind,
  turnId: string,
  contextInput: LineSignalContextInput = {},
): void => {
  if (!isLineSignalCaptureEnabled()) return
  const workspaceState = contextInput.workspaceId ? workspaceCaptureState.get(contextInput.workspaceId) : undefined
  if (workspaceState && (workspaceState.paused || !workspaceState.enabled || workspaceState.killSwitch)) return
  const axes = coarsenContextAxes(contextInput)
  if (!isCoarsenedContextAxes(axes)) {
    if (import.meta.env.DEV) console.error('[line-learning] rejected unsafe signal axes')
    return
  }
  const signal: RawLineSignal = {
    id: createSignalId(),
    kind,
    turnId: turnId.slice(0, 128),
    emittedAt: Date.now(),
    axes,
    flushed: false,
    attempt: 0,
  }
  void pushLineSignal(signal)
}
